import type { ReactNode, SVGProps } from 'react';

/**
 * Icons that are not Font Awesome glyphs, by semantic name. Mirrors the design system's custom-icon
 * registry (`components/icon/custom-icons.js`), copied verbatim: a mark is saved here rather than drawn
 * inside a feature, so `Icon` renders every icon by name and the Storybook gallery shows the whole set.
 *
 * Each entry follows the design registry's rules:
 * - `svg` is the root's presentation attributes: a `viewBox` and no width/height, because `Icon` sizes
 *   the mark to its square `1em` box;
 * - stroke and fill are `currentColor` only, never a literal colour, so `text-*` utilities tint it;
 * - no `<title>`: `Icon` owns accessibility (decorative unless given a `label`);
 * - a camelCase name in the glyph map's vocabulary that doesn't shadow a Font Awesome name.
 */
export interface CustomIcon {
  svg: Pick<
    SVGProps<SVGSVGElement>,
    'viewBox' | 'fill' | 'stroke' | 'strokeWidth' | 'strokeLinecap' | 'strokeLinejoin'
  >;
  paths: ReactNode;
}

/**
 * The health-theme marks' drawing convention: stroke-only on a 32-unit artboard, weight 1.5 (the weight
 * that sits beside a Font Awesome regular glyph without reading heavier), with interior detail on a
 * second layer at 1.1 and 0.55 opacity.
 */
const THEME_MARK: CustomIcon['svg'] = {
  viewBox: '0 0 32 32',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

export const CUSTOM_ICONS = {
  // The five AWP health themes. None exists as a Font Awesome glyph.
  themeSummary: {
    svg: THEME_MARK,
    paths: (
      <>
        <path d="M17 4.5H8.5A1.5 1.5 0 0 0 7 6v20a1.5 1.5 0 0 0 1.5 1.5H20a1.5 1.5 0 0 0 1.5-1.5V9L17 4.5z" />
        <path d="M19.5 27.5l.9-3.2 5.2-5.2a1.6 1.6 0 0 1 2.3 2.3l-5.2 5.2-3.2.9z" />
        <path d="M17 4.5V9h4.5M11 14.5h6.5M11 18.5h6.5M11 22.5h4" strokeWidth={1.1} opacity={0.55} />
      </>
    ),
  },
  themeHeart: {
    svg: THEME_MARK,
    paths: (
      <>
        <path d="M16 27s-9-6-9-13.5a5.5 5.5 0 0110-3.2 5.5 5.5 0 0110 3.2C27 21 16 27 16 27z" />
        <path d="M6.6 15.5h4.6l1.8 4.2 2.4-8.6 2 4.4h8.1" />
      </>
    ),
  },
  themeMetabolic: {
    svg: THEME_MARK,
    paths: (
      <>
        <path d="M14.26 6.15A10 10 0 0 0 14.26 25.85" />
        <path d="M11.44 5.12 14.26 6.15 11.96 8.08" />
        <path d="M17.74 25.85A10 10 0 0 0 17.74 6.15" />
        <path d="M20.56 26.88 17.74 25.85 20.04 23.92" />
        <path d="M16 10.5c2.6 3.2 4.2 5.1 4.2 7.1a4.2 4.2 0 0 1-8.4 0c0-2 1.6-3.9 4.2-7.1z" />
        <path
          d="M16 16.4c1.2 1.5 1.9 2.3 1.9 3.2a1.9 1.9 0 0 1-3.8 0c0-.9.7-1.7 1.9-3.2z"
          strokeWidth={1.1}
          opacity={0.55}
        />
      </>
    ),
  },
  themeBrain: {
    svg: THEME_MARK,
    paths: (
      <>
        <path d="M16 6.2a4.2 4.2 0 0 0-7.1 2.6A4 4 0 0 0 6.6 15a4.2 4.2 0 0 0 1.6 6.4A4 4 0 0 0 12 26a3.6 3.6 0 0 0 4-1.6 3.6 3.6 0 0 0 4 1.6 4 4 0 0 0 3.8-4.6A4.2 4.2 0 0 0 25.4 15a4 4 0 0 0-2.3-6.2A4.2 4.2 0 0 0 16 6.2z" />
        <path
          d="M16 7.6v16.8M11.6 11.8c1.4.9 2.7 1 3.6.5M20.4 11.8c-1.4.9-2.7 1-3.6.5M11.2 16.2c1.6.6 3 .5 4-.3M20.8 16.2c-1.6.6-3 .5-4-.3M11.6 20.6c1.4-.9 2.7-1 3.6-.5M20.4 20.6c-1.4-.9-2.7-1-3.6-.5"
          strokeWidth={1.1}
          opacity={0.55}
        />
      </>
    ),
  },
  themeLongevity: {
    svg: THEME_MARK,
    paths: (
      <>
        <path d="M8.5 22.5a7.5 7.5 0 0 1 15 0" />
        <path d="M3 22.5h26" />
        <path d="M16 12.3V9M8.79 15.29 6.45 12.95M23.21 15.29l2.34-2.34M6.58 18.6 3.53 17.33M25.42 18.6l3.05-1.27" />
        <path d="M7.5 27.5h17" strokeWidth={1.1} opacity={0.55} />
      </>
    ),
  },
} as const satisfies Record<string, CustomIcon>;
